import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GQLMerchantDeliveryType,
  GQLParty,
  GQLPlatformMerchantOrder,
  GQLPlatformProducerOrder
} from '@jewelutions/core-crm-server-api-client-domain';

import { JwlTable, LoadingSpinner, ErrorComponent } from '../../../../components';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { GQLAddressHelper } from '../../../../operations/helpers/GQLAddressHelper';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';

interface IProps {}

const PlatformProducerWithPlatformMerchantOrdersList: React.FC<IProps> = ({}: IProps) => {
  const history = useHistory();

  const [filteredOrders, setFilteredOrders] = useState('');

  const {
    data: ordersData,
    loading: ordersLoading
  } = GQLOperationBuilder.platform.getAllPlatformMerchantOrderWithPlatformProducerOrders({});

  if (ordersLoading) return <LoadingSpinner />;

  const handleFilterChange = (event: any) => setFilteredOrders(event.target.value);

  const getMerchantOrderName = (order: GQLPlatformMerchantOrder) =>
    order.merchantDelivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? order.merchantDelivery.warehouseDelivery!.companyName
      : `${order.merchantDelivery.dropshipDelivery!.personFirstName} ${
          order.merchantDelivery.dropshipDelivery!.personLastName
        }`;

  const getMerchantOrderLocation = (order: GQLPlatformMerchantOrder) =>
    order.merchantDelivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? GQLAddressHelper.addressToString(order.merchantDelivery.warehouseDelivery!.address!)
      : GQLAddressHelper.addressToString(order.merchantDelivery.dropshipDelivery!.address!);

  const getOrderRows = () => {
    return GQLOperationBuilder.platform
      .platformMerchantOrderWithPlatformProducerOrderArrayFromResponse(ordersData)
      .filter(order => {
        return (
          getMerchantOrderName(order.platformMerchantOrder)
            .toLowerCase()
            .includes(filteredOrders.toLowerCase().trim()) ||
          getMerchantOrderLocation(order.platformMerchantOrder)
            .toLowerCase()
            .includes(filteredOrders.toLowerCase().trim())
        );
      })
      .map(order => {
        return {
          id: {
            platformMerchantOrderId: order.platformMerchantOrder.id,
            platformProducerOrderId: order.platformProducerOrder?.id
          },
          cells: [
            order.platformMerchantOrder.id,
            getMerchantOrderLocation(order.platformMerchantOrder),
            order.platformMerchantOrder.state,
            order.platformProducerOrder?.producerDeliveryUnit.name,
            order.platformProducerOrder?.state,
            new Date(Date.parse(order.platformMerchantOrder.createdAt)).toLocaleString(),
            new Date(Date.parse(order.platformMerchantOrder.updatedAt)).toLocaleString()
          ]
        };
      });
  };

  const navigateToOrderDetails = (id: {
    platformMerchantOrderId: string;
    platformProducerOrderId: string;
  }) => {
    history.push(
      PlatformNavigation.platformProducerWithPlatformMerchantOrderDetails.pathFromParams({
        platformMerchantOrderId: id.platformMerchantOrderId,
        platformProducerOrderId: id.platformProducerOrderId
      })
    );
  };

  return (
    <Fragment>
      <JwlTable
        title="Platform order pair list"
        description={`The list of all the order pairs defined in the platform. Each pair has two types of orders :  "Merchant -> Platform" & "Platform -> Producer". The platform(s) role is to make sure the order flow between merchants and producers is managed correctly.`}
        columns={[
          'ID',
          'Merchant Order Delivery',
          'Merchant Order State',
          'Producer Delivery Unit',
          'Producer Order State',
          'Merchant Order Created At',
          'Merchant Updated Order At'
        ]}
        rows={getOrderRows()}
        onFilterChange={handleFilterChange}
        onTableRowClick={navigateToOrderDetails}
      />
    </Fragment>
  );
};

export default PlatformProducerWithPlatformMerchantOrdersList;
